import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { UtilsHelper } from '@helpers/utils.helper';
import { Device } from '@models/device';
import { UpdateAttribute } from '@models/updateAttribute.model';
import { AttributeNewDeviceService } from '@services/attribute-new-device.service'; // importa el servicio
import { ToastService } from '@shared/services/toast.service';

@Component({
  standalone: false,
  selector: 'app-stack-settings',
  templateUrl: './stack-settings.component.html',
  styleUrls: ['./stack-settings.component.scss'],
})

export class StackSettingsComponent implements OnInit, OnChanges {
  @Input() device!: Device;
  @Output() updated = new EventEmitter<void>();

  stackForm!: FormGroup;
  submitted = false;
  initialFormValue: any;
  availableSensors: { id: number; name: string; icon?: string }[][] = [];

  constructor(
    private fb: FormBuilder,
    private attributeService: AttributeNewDeviceService,
    private toastService: ToastService,

  ) { }

  ngOnInit() {
    if (this.device) {
      this.buildForm();
      this.updateFormStateByConnectivity();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['device'] && this.device) {
      this.buildForm();
      this.updateFormStateByConnectivity();

    }
  }

  private buildForm(): void {
    const formControlsConfig: any = {
      sendPeriod: [
        parseInt(
          this.device?.extraInfo?.newPeriod?.value ||
          this.device?.extraInfo?.period?.value ||
          '15',
          10
        ),
        [Validators.required, Validators.min(1)],
      ]
    };

    this.device?.sensors?.forEach((sensor, index) => {
      const nodeKey = `node${index + 1}`;
      const sondaKey = `sonda${index + 1}`;

      const newPeriodValue = sensor.extraInfo?.newPeriod?.value;
      const fallbackPeriodValue = sensor.extraInfo?.period?.value;
      const period = parseInt(newPeriodValue || fallbackPeriodValue || '60', 10);

      // módulo del node
      const moduleConnected = sensor.extraInfo?.moduleConnected?.value ?? '0';
      this.availableSensors[index] =
        UtilsHelper.getAvailableSensorsByModule(moduleConnected);


      const currentSensorInterno =
        sensor.extraInfo?.newProbeConnected?.value ??
        sensor.extraInfo?.probeConnected?.value;

      formControlsConfig[nodeKey] = [
        period,
        [Validators.required, Validators.min(60)]
      ];

      formControlsConfig[sondaKey] = [
        Number(currentSensorInterno),
        Validators.required
      ];
    });


    this.stackForm = this.fb.group(formControlsConfig);
    this.initialFormValue = this.deepCopy(this.stackForm.value);
  }

  get nodeControls() {
    return this.device?.sensors?.map((_, i) => this.stackForm.get(`node${i + 1}`)) || [];
  }

  getUpdatedNodePeriod(index: number): number {
    const value = this.stackForm?.get(`node${index}`)?.value;
    return value ? value * 2 : 0;
  }

  get calculatedSendPeriod(): number {
    const val = this.stackForm?.get('sendPeriod')?.value;
    return val ? val * 2 : 0;
  }

  onSubmit(): void {
    this.submitted = true;
    if (this.stackForm.invalid) return;

    const payload = this.generateChangedAttributes();

    if (payload.length) {
      this.attributeService.updateAttributes(payload).subscribe({
        next: () => {
          this.updated.emit();
          this.initialFormValue = this.deepCopy(this.stackForm.value);
          this.stackForm.markAsPristine();
          this.toastService.successToast('Dispositivo actualizado');
        },
        error: (err) => {
          this.toastService.errorToast('Ha habido un error al actualizar los datos. Inténtelo de nuevo más tarde');
        }
      });
    }
  }

  private generateChangedAttributes(): UpdateAttribute[] {
    const changes: UpdateAttribute[] = [];

    // Verificar cambios en sendPeriod
    const currentSend = this.stackForm.get('sendPeriod')?.value;
    const initialSend = this.initialFormValue['sendPeriod'];

    if (currentSend !== initialSend) {
      changes.push({
        id: this.device.id,
        type: '0',
        attribute: 'newPeriod',
        value: String(currentSend)
      });
    }

    // Verificar cambios en nodos
    this.device?.sensors?.forEach((sensor, index) => {
      const controlKey = `node${index + 1}`;
      const currentValue = this.stackForm.get(controlKey)?.value;
      const initialValue = this.initialFormValue[controlKey];

      if (currentValue !== initialValue) {
        changes.push({
          id: sensor.id,
          type: '1',
          attribute: 'newPeriod',
          value: String(currentValue)
        });
      }
    });

    // Verificar cambios en sonda
    this.device?.sensors?.forEach((sensor, index) => {
      const key = `sonda${index + 1}`;

      const currentValue = this.stackForm.get(key)?.value;
      const initialValue = this.initialFormValue[key];

      if (currentValue !== initialValue) {
        changes.push({
          id: sensor.id,
          type: '1', // SENSOR
          attribute: 'newProbeConnected',
          value: String(currentValue)
        });
      }
    });


    return changes;
  }

  hasFormChanged(): boolean {
    return JSON.stringify(this.stackForm.value) !== JSON.stringify(this.initialFormValue);
  }

  private deepCopy(obj: any): any {
    return JSON.parse(JSON.stringify(obj));
  }

  hasNewPeriods(): boolean {
    // Verifica si el dispositivo principal tiene un newPeriod
    const mainHasNew = !!this.device?.extraInfo?.newPeriod?.value;

    // Verifica si alguno de los sensores tiene un newPeriod
    const sensorsHaveNew = this.device?.sensors?.some(
      sensor => !!sensor.extraInfo?.newPeriod?.value
    ) ?? false;

    return mainHasNew || sensorsHaveNew;
  }

  getEstimatedPeriod(): number {
    return parseInt(this.device?.extraInfo?.period?.value || '30', 10);
  }

  get isBluetoothMode(): boolean {
    // Asumiendo que tienes device?.extraInfo?.isActived?.value indicando Bluetooth
    // O puedes usar otro campo, como device?.connectivity === 'ble'
    return this.device?.extraInfo?.isActived?.value === 'false';
  }


  private updateFormStateByConnectivity() {
    if (!this.stackForm) return;
    if (this.isBluetoothMode) {
      this.stackForm.get('sendPeriod')?.disable({ emitEvent: false });
      this.device?.sensors?.forEach((_, i) => {
        this.stackForm.get('node' + (i + 1))?.disable({ emitEvent: false });
      });
    } else {
      this.stackForm.get('sendPeriod')?.enable({ emitEvent: false });
      this.device?.sensors?.forEach((_, i) => {
        this.stackForm.get('node' + (i + 1))?.enable({ emitEvent: false });
      });
    }
  }

private normalizeVersion(v?: string): number[] {
  const clean = (v ?? '0.0.0').trim().replace(/^v/i, '');
  return clean.split('.').map(x => parseInt(x, 10)).map(n => (Number.isFinite(n) ? n : 0));
}

private compareSemver(a?: string, b?: string): number {
  const pa = this.normalizeVersion(a);
  const pb = this.normalizeVersion(b);

  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const na = pa[i] ?? 0;
    const nb = pb[i] ?? 0;
    if (na > nb) return 1;
    if (na < nb) return -1;
  }
  return 0;
}
//mostrar la columna solo si todos los sensores cumplen con la versión
get showSondaColumn(): boolean {
  const sensors = this.device?.sensors ?? [];
  if (!sensors.length) return false;

  return sensors.every(s => {
    const v = s?.extraInfo?.softwareVersion?.value;
    return !!v && this.compareSemver(v, '1.5.0.0') >= 0;
  });
}


}


